<template>
  <h3 class="h3" v-if="totalsByStatusDisplayValue">
    {{ t('reports.cases.show.totals_by_status', { status_totals: totalsByStatusDisplayValue }) }}
  </h3>
  <data-grid-remote
    :total-rows="fetchTotalRows"
    :server-url="serverUrl"
    :data-fetch-callback-fn="dataFetchCallbackFn"
    :fetchOptions="fetchOptions"
    :useFetchOptions="useFetchOptions"
    :pageSize="pageSize"
    :currentPage="currentPage"
    :paginationOptions="paginationOptions"
    @update:currentPage="onPageUpdate"
    @update:pageSize="onPageSizeUpdate"
    @update:sort="onSortChange"
    data-test="remote-grid"
  >
    <template v-slot:header>
      <div class="filters-container">
        <div class="filter-bar-container">
          <f-filter-bar
            v-model="filterValue"
            @update:modelValue="updateFilters"
            data-test="filter-bar"
          >
            <f-filter-bar-category
              v-if="filters.case_types?.length > 1"
              :title="t('activerecord.models.case_type', { count: 1 })"
              name="case_type_ids"
              :is-searchable="filters.case_types.length > 5"
              is-dialog-persisted
            >
              <f-filter-bar-option
                v-for="(case_type, optionIndex) in filters.case_types"
                :key="optionIndex"
                :title="case_type.name"
                :value="case_type.id"
              />
            </f-filter-bar-category>

            <f-filter-bar-category
              v-if="filters.schools?.length > 1"
              :title="t('activerecord.models.school', { count: 1 })"
              name="school_ids"
              :is-searchable="filters.schools.length > 5"
              is-dialog-persisted
            >
              <f-filter-bar-option
                v-for="(school, optionIndex) in filters.schools"
                :key="optionIndex"
                :title="school.name"
                :value="school.id"
              />
            </f-filter-bar-category>

            <f-filter-bar-category
              v-if="filters.statuses?.length > 1"
              :title="t('activerecord.attributes.case.status')"
              name="statuses"
              :is-searchable="filters.statuses.length > 5"
              is-dialog-persisted
            >
              <f-filter-bar-option
                v-for="(status, optionIndex) in filters.statuses"
                :key="optionIndex"
                :title="status"
                :value="status"
              />
            </f-filter-bar-category>

            <f-filter-bar-category
              v-if="filters.assignees?.length > 1"
              :title="t('activerecord.attributes.case.assignees', { count: 'many' })"
              name="assignee_ids"
              :is-searchable="filters.assignees.length > 5"
              is-dialog-persisted
            >
              <f-filter-bar-option
                :title="t('reports.cases.show.filters.case_assignees.options.unassigned')"
                value="unassigned"
              />
              <f-filter-bar-option
                v-for="(assignee, optionIndex) in filters.assignees"
                :key="optionIndex"
                :title="assignee.name"
                :value="assignee.id"
              />
            </f-filter-bar-category>

            <f-filter-bar-category
              v-if="filters.severities?.length"
              :title="t('activerecord.attributes.case.severity')" name="severities"
              :is-searchable="filters.severities.length > 5"
              :is-dialog-persisted="filters.severities.length > 1"
            >
              <f-filter-bar-option
                v-for="(severity, optionIndex) in filters.severities"
                :key="optionIndex"
                :title="severity"
                :value="severity"
              />
            </f-filter-bar-category>

            <f-filter-bar-category
              v-if="filters.creators?.length > 1"
              :title="t('reports.cases.show.created_by')"
              name="creator_ids"
              :is-searchable="filters.creators.length > 5"
              is-dialog-persisted
            >
              <f-filter-bar-option
                v-for="(creator, optionIndex) in filters.creators"
                :key="optionIndex"
                :title="creator.name"
                :value="creator.id"
              />
            </f-filter-bar-category>

            <f-filter-bar-category
              v-if="filters.students?.length > 1"
              :title="capitalize(t('student'))"
              name="student_ids"
              :is-searchable="filters.students.length > 5"
              is-dialog-persisted
            >
              <f-filter-bar-option
                v-for="(student, optionIndex) in filters.students"
                :key="optionIndex"
                :title="student.name"
                :value="student.id"
              />
            </f-filter-bar-category>

            <f-filter-bar-category
              v-if="filters.groups?.length"
              :title="mainGroup" name="group_ids"
              :is-searchable="filters.groups.length > 5"
              :is-dialog-persisted="filters.groups.length > 1"
            >
              <f-filter-bar-option
                v-for="(group, optionIndex) in filters.groups"
                :key="optionIndex"
                :title="group.name"
                :value="group.id"
              />
            </f-filter-bar-category>

            <f-filter-bar-category
              v-if="filters.ethnicities?.length"
              :title="t('activerecord.models.ethnicity', { count: 1 })"
              name="ethnicity_ids"
              :is-searchable="filters.ethnicities.length > 5"
              :is-dialog-persisted="filters.ethnicities.length > 1"
            >
              <f-filter-bar-option
                v-for="(ethnicity, optionIndex) in filters.ethnicities"
                :key="optionIndex"
                :title="ethnicity.name"
                :value="ethnicity.id"
              />
            </f-filter-bar-category>

            <f-filter-bar-category
              v-if="filters.genders?.length"
              :title="t('activerecord.attributes.student.gender')"
              name="genders"
              :is-searchable="filters.genders.length > 5"
              :is-dialog-persisted="filters.genders.length > 1"
            >
              <f-filter-bar-option
                v-for="(gender, optionIndex) in filters.genders"
                :key="optionIndex"
                :title="gender"
                :value="gender"
              />
            </f-filter-bar-category>

            <f-filter-bar-category
              v-if="filters.categories?.length"
              :title="t('activerecord.models.category', { count: 'many' })"
              name="category_ids"
              :is-searchable="filters.categories.length > 5"
              :is-dialog-persisted="filters.categories.length > 1"
            >
              <f-filter-bar-option
                v-for="(category, optionIndex) in filters.categories"
                :key="optionIndex"
                :title="category.name"
                :value="category.id"
              />
            </f-filter-bar-category>

            <f-filter-bar-category
              :title="t('reports.cases.show.filters.case_opened_or_closed.title')"
              name="closed_at_method"
            >
              <f-filter-bar-option
                :title="t('reports.cases.show.filters.case_opened_or_closed.options.open')"
                value="never"
              />

              <f-filter-bar-option
                :title="t('reports.cases.show.filters.case_opened_or_closed.options.closed')"
                value="ever"
              />
            </f-filter-bar-category>

            <f-filter-bar-category :title="t('reports.cases.show.filters.incident_reported.title')" name="incident-created">
              <f-filter-bar-date
                :title="t('reports.cases.show.filters.incident_reported.options.from.title')"
                name="incident_created_at_from"
                :max="incidentCreatedAtTo || new Date()"
                :active-text-fn="(text) => t('reports.cases.show.filters.incident_reported.options.from.active_text', { date: text })"
              />
              <f-filter-bar-date
                :title="t('reports.cases.show.filters.incident_reported.options.to.title')"
                name="incident_created_at_to"
                :min="incidentCreatedAtFrom"
                :max="new Date()"
                :active-text-fn="(text) => t('reports.cases.show.filters.incident_reported.options.to.active_text', { date: text })"
              />
            </f-filter-bar-category>

            <f-filter-bar-category :title="t('reports.cases.show.filters.case_opened_date.title')" name="case-opened">
              <f-filter-bar-date
                :title="t('reports.cases.show.filters.case_opened_date.options.from.title')"
                name="created_at_from"
                :max="createdAtTo || new Date()"
                :active-text-fn="(text) => t('reports.cases.show.filters.case_opened_date.options.from.active_text', { date: text })"
              />
              <f-filter-bar-date
                :title="t('reports.cases.show.filters.case_opened_date.options.to.title')"
                name="created_at_to"
                :min="createdAtFrom"
                :max="new Date()"
                :active-text-fn="(text) => t('reports.cases.show.filters.case_opened_date.options.to.active_text', { date: text })"
              />
            </f-filter-bar-category>

            <f-filter-bar-category :title="t('reports.cases.show.filters.case_closed_date.title')" name="case-closed">
              <f-filter-bar-date
                :title="t('reports.cases.show.filters.case_closed_date.options.from.title')"
                name="from_date"
                :max="closedAtTo || new Date()"
                :active-text-fn="(text) => t('reports.cases.show.filters.case_closed_date.options.from.active_text', { date: text })"
              />
              <f-filter-bar-date
                :title="t('reports.cases.show.filters.case_closed_date.options.to.title')"
                name="to_date"
                :min="closedAtFrom"
                :max="new Date()"
                :active-text-fn="(text) => t('reports.cases.show.filters.case_closed_date.options.to.active_text', { date: text })"
              />
            </f-filter-bar-category>
          </f-filter-bar>
        </div>
      </div>
    </template>

    <data-grid-column
      :title="t('reports.cases.show.open_case')"
      data-id="case_path"
      v-slot="value"
    >
      <a class="btn btn--primary-alt feather_btn feather_btn--primary-alt" :href="value">{{ t('reports.cases.show.link_to_case') }}</a>
    </data-grid-column>

    <data-grid-column
      :title="t('activerecord.models.school', { count: 1 })"
      data-id="school_name"
      v-slot="value"
      sortable
    >
      {{ value }}
    </data-grid-column>

    <data-grid-column
      :title="t('reports.cases.show.student_name', { student: capitalize(t('student')) })"
      data-id="student_name"
      v-slot="value"
      sortable
    >
      <a :href="value.path">{{ value.name }}</a>
    </data-grid-column>

    <data-grid-column
      :title="t('activerecord.models.case_type', { count: 1 })"
      data-id="case_type_name"
      v-slot="value"
      sortable
    >
      {{ value }}
    </data-grid-column>

    <data-grid-column
      :title="t('activerecord.models.category', { count: 'many' })"
      data-id="categories"
      v-slot="value"
    >
      <div class="hidden-xs">
        <div class="category-items-container category-items-container-cell">
          <div
            v-for="category in value"
            class="category-badge"
            :style="`background-color: ${category.color}; color:white;`"
          >
            <span>{{ category.name }}</span>
          </div>
        </div>
      </div>

      <div class="visible-xs">
        <div class="category-items-container pl-1 pr-1">
          <span
            v-for="category in value"
            class="category-dot"
            :title="category.name"
            :style="`background: ${category.color}`"
            data-toggle="tooltip"
            data-placement="bottom"
          >
          </span>
        </div>
        <f-button
          v-if="value.length > 0"
          text="See Categories"
          type="tertiary"
          class="pl-1 pr-1"
          @click="() => openCategoriesModal(value)"
        >
        </f-button>
      </div>
    </data-grid-column>

    <data-grid-column
      :title="t('reports.cases.show.incident_date')"
      data-id="incident_date"
      v-slot="value"
      sortable
    >
      <date-formatted v-if="value" :value="new Date(value)" pattern="date-time"></date-formatted>
      <span v-else>{{ t('reports.common.values.not_available') }}</span>
    </data-grid-column>

    <data-grid-column
      :title="t('reports.cases.show.case_opened_date')"
      data-id="opened_at"
      v-slot="value"
      sortable
    >
      <date-formatted v-if="value" :value="new Date(value)" pattern="date-time"></date-formatted>
      <span v-else>{{ t('reports.common.values.not_available') }}</span>
    </data-grid-column>

    <data-grid-column
      :title="t('reports.cases.show.created_by')"
      data-id="creator"
      v-slot="value"
      sortable
    >
      {{ value }}
    </data-grid-column>

    <data-grid-column
      :title="t('activerecord.attributes.case.status')"
      data-id="status"
      v-slot="value"
      sortable
    >
      {{ value }}
    </data-grid-column>

    <data-grid-column
      :title="t('activerecord.attributes.case.assignees', { count: 'many' })"
      data-id="assignees"
      v-slot="value"
      sortable
    >
      {{ value || t('reports.cases.show.values.case_assignees.unassigned') }}
    </data-grid-column>

    <data-grid-column
      :title="t('activerecord.attributes.case.severity')"
      data-id="severity"
      v-slot="value"
      sortable
    >
      {{ value || t('reports.common.values.not_available') }}
    </data-grid-column>

    <data-grid-column
      :title="t('reports.cases.show.case_closed_date')"
      data-id="closed_at"
      v-slot="value"
      sortable
    >
      <date-formatted v-if="value" :value="new Date(value)" pattern="date-time"></date-formatted>
      <span v-else>{{ t('reports.common.values.not_available') }}</span>
    </data-grid-column>

    <data-grid-column
      :title="mainGroup" data-id="main_group"
      v-slot="value"
      sortable
    >
      {{ value }}
    </data-grid-column>

    <data-grid-column
      :title="t('reports.cases.show.age')"
      data-id="student_age"
      v-slot="value"
      sortable
    >
      {{ value }}
    </data-grid-column>

    <data-grid-column
      :title="t('activerecord.models.ethnicity', { count: 1 })"
      data-id="student_ethnicity"
      v-slot="value"
      sortable
    >
      {{ value }}
    </data-grid-column>

    <data-grid-column
      :title="t('activerecord.attributes.student.gender')"
      data-id="student_gender"
      v-slot="value"
      sortable
    >
      {{ value }}
    </data-grid-column>
  </data-grid-remote>

  <modal-box
    v-if="isCategoriesModalVisible"
    title="Categories"
    :is-default-footer-used="false"
    @close="hideModal"
    data-test="categories-modal"
  >
    <div class="category-items-container category-items-container-modal">
      <template v-for="category in selectedCategories">
        <div
          class="category-badge"
          :style="`background-color: ${category.color}; color:white;`"
        >
          <span>{{ category.name }}</span>
        </div>
      </template>
    </div>
  </modal-box>
</template>

<script setup>
import { computed, ref } from 'vue';
import {
  DataGridColumn,
  DataGridRemote,
  DateFormatted,
  FFilterBar,
  FFilterBarCategory,
  FFilterBarDate,
  FFilterBarOption,
  FButton,
  ModalBox,
} from '@raptor/feather-ui';
import { capitalize } from '~/helpers/string_helpers';
import { toDate } from '~/helpers/date_helpers';
import { csrf } from '~/helpers/application_helpers.js';

// helper methods
const { t } = I18n;

const props = defineProps({
  dataUrl: String,
  filters: Object,
  mainGroup: String,
  defaultFilters: Object,
  defaultSort: Object,
});

const DATE_FILTER_NAMES = [
  'created_at_from',
  'created_at_to',
  'incident_created_at_from',
  'incident_created_at_to',
  'from_date',
  'to_date',
]

const formattedDefaultFilters =  Object.entries(props.defaultFilters).reduce((newObj, [key, value]) => {
  newObj[key] = DATE_FILTER_NAMES.includes(key) ? [toDate(value)] : [value]
  return newObj;
}, {});

// Total cases by status
const totalsByStatus = ref({});
const totalsByStatusDisplayValue = computed(() => {
  const entries = Object.entries(totalsByStatus.value);

  if (!entries.length) return null;

  return entries
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ');
});

// Filters
const filterValue = ref(formattedDefaultFilters);
const valueForField = (field) => filterValue.value[field]?.[0];
const createdAtFrom = computed(() => valueForField('created_at_from'));
const createdAtTo = computed(() => valueForField('created_at_to'));
const closedAtFrom = computed(() => valueForField('from_date'));
const closedAtTo = computed(() => valueForField('to_date'));
const incidentCreatedAtFrom = computed(() => valueForField('incident_created_at_from'));
const incidentCreatedAtTo = computed(() => valueForField('incident_created_at_to'));
const closedAtMethod = computed(() => valueForField('closed_at_method'));
const assigneeIds = computed(() => filterValue.value.assignee_ids?.filter((v) => v !== 'unassigned'))
const includeUnassigned = computed(() => filterValue.value.assignee_ids?.includes('unassigned'))

// Sorting
const sortBy = ref(props.defaultSort?.sort_by);
const sortOrder = ref(props.defaultSort?.sort_order);

const onSortChange = (newValue) => {
  const primarySort = newValue.at(-1);
  sortBy.value = primarySort.dataId;
  sortOrder.value = primarySort.direction;
};

// Pagination
const paginationOptions = {
  perPageOptions: [5, 10, 15, 20],
};

const pageSize = ref(10);

const onPageSizeUpdate = (newPageSize) => {
  pageSize.value = newPageSize;
};

const currentPage = ref(1);

const onPageUpdate = (newPageNumber) => {
  currentPage.value = newPageNumber;
};

const totalRows = ref(0);
const fetchTotalRows = computed(() => () => totalRows.value);

// Categories Modal
const isCategoriesModalVisible = ref(false);
const selectedCategories = ref([]);
const openCategoriesModal = (categories) => {
  selectedCategories.value = categories;
  isCategoriesModalVisible.value = true;
};
const hideModal = () => {
  isCategoriesModalVisible.value = false;
  selectedCategories.value = [];
};

// Fetch related options
const serverUrl = () => {
  return props.dataUrl;
};

const useFetchOptions = {
  beforeFetch({ options }) {
    options.headers = {
      ...options.headers,
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf(),
    };
    return { options };
  },
};

const filtersForRequest = computed(() => ({
  ...filterValue.value,
  assignees_ids: assigneeIds.value,
  include_unassigned: includeUnassigned.value,
  created_at_from: createdAtFrom.value,
  created_at_to: createdAtTo.value,
  from_date: closedAtFrom.value,
  to_date: closedAtTo.value,
  incident_created_at_from: incidentCreatedAtFrom.value,
  incident_created_at_to: incidentCreatedAtTo.value,
  closed_at_method: closedAtMethod.value,
}));

const fetchOptions = computed(() => ({
  method: 'post',
  body: {
    page: currentPage.value,
    per_page: pageSize.value,
    filters: filtersForRequest.value,
    sort_by: sortBy.value,
    sort_order: sortOrder.value,
  },
}));

const updateFilters = () => {
  const filters = filterValue.value;
  DATE_FILTER_NAMES.forEach((name) => {
    filters[name] = filters[name]?.slice(-1);
  })

  filters.closed_at_method = filters.closed_at_method?.slice(-1) || [];
};

// response handling
const dataFetchCallbackFn = (payload) => {
  if (!payload) return;

  const { totals_by_status, data, total } = payload;

  totalRows.value = total || 0;
  totalsByStatus.value = totals_by_status || {};

  return data.cases?.map((c) => ({
    case_type_name: c.case_type,
    categories: c.categories,
    closed_at: c.closed_at,
    student_ethnicity: c.student.ethnicity,
    student_gender: c.student.gender,
    id: c.id,
    incident_date: c.incident_date,
    main_group: c.student.main_group,
    opened_at: c.opened_at,
    creator: c.creator,
    school_name: c.school_name,
    severity: c.severity,
    status: c.status,
    assignees: (c.assignees || []).join(', '),
    student_age: c.student.age,
    student_name: {
      name: c.student.name,
      path: c.student.path,
    },
    case_path: c.path
  })) || [];
};
</script>

<style lang="scss" scoped>
@import "@raptor/feather-ui/_base.scss";

.filters-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.category-items-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  .category-badge {
    display: inline-flex;
    border-radius: 1em;
    padding: 4px 8px;
    font-size: 85%;
    line-height: 1;
    text-align: center;

    align-items: center;
    justify-content: center;
  }

  .category-dot {
    display: flex;
    width: 1.5em;
    height: 1.5em;
    border-radius: 1em;
  }
}

.category-items-container-cell {
  width: 250px;
}

// Modal needs to have scrolling if there are many categories
.category-items-container-modal {
  overflow-y: auto;
  max-height: 35vh;
  padding-right: 15px;
  margin-right: -15px;
}

// Override the global modal rule is required
// .modal is default class from feather-ui ModalBox component
// but our cpoms css has a rule that is interfering with the modal
:deep(.modal) {
  width: min(75dvw, 90%) !important;
  height: fit-content;
  position: fixed;

  &:before {
    content: none;
  }

  .modal-header__title {
    text-align: left;
  }
}

// Override feather-ui filter bar styles for small screens
// This is only required because the filter bar was going over width of
// the screen on smaller resolutions, which causes the modal to also go over.
@media (max-width: 767px){
  :deep(.f-filter-bar) {
      display: block !important;
  }

  :deep(.filter-button),
  :deep(.active-bar-button),
  :deep(.more-filters .more-filters_toggle) {
    margin-left: 0 !important;
    margin-right: 8px !important;
  }

  :deep(.clear-all-btn) {
    padding-left: 0 !important;
  }
}
</style>
